import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  PhoneCall, 
  Check, 
  CreditCard,
  ArrowLeft,
  Loader2,
  Lock,
  Users,
  Minus,
  Plus,
  ShieldCheck,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE_URL } from '../utils/api';
const API = API_BASE_URL;

export default function Subscribe() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: Plan, 2: Payment
  const [userCount, setUserCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState(null);
  
  // Check for upgrade reason from URL
  useEffect(() => {
    const reason = searchParams.get('reason');
    if (reason === 'user_limit') {
      setUpgradeReason('user_limit');
    }
  }, [searchParams]);
  
  // Payment form state (mock)
  const [paymentForm, setPaymentForm] = useState({
    cardNumber: '',
    expiry: '',
    cvc: '',
    name: '',
    email: '',
    companyName: ''
  });

  const pricePerUser = 15;
  const totalPrice = userCount * pricePerUser;

  const features = [
    'Unlimited calls',
    'WebRTC softphone',
    'Call history & recordings',
    'Team presence',
    'Queue monitoring',
    'Analytics dashboard',
    'API access',
    'Priority support'
  ];

  const handlePayment = async () => {
    // Validate form
    if (!paymentForm.email || !paymentForm.companyName || !paymentForm.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!paymentForm.cardNumber || !paymentForm.expiry || !paymentForm.cvc) {
      toast.error('Please enter your payment details');
      return;
    }

    setLoading(true);

    try {
      // Mock Stripe payment - simulate processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Create subscription in backend
      const response = await axios.post(`${API}/api/subscriptions/create`, {
        company_name: paymentForm.companyName,
        admin_email: paymentForm.email,
        admin_name: paymentForm.name,
        user_count: userCount,
        plan: 'pro',
        // Mock Stripe IDs
        stripe_customer_id: `cus_mock_${Date.now()}`,
        stripe_subscription_id: `sub_mock_${Date.now()}`
      });

      toast.success('Subscription created successfully!');
      
      // Redirect to onboarding with the company token
      navigate(`/onboarding?token=${response.data.onboarding_token}`);
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  // Format expiry as MM/YY
  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.slice(0, 2) + (v.length > 2 ? '/' + v.slice(2, 4) : '');
    }
    return v;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <PhoneCall className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white tracking-tight">CONNECT</span>
          </Link>
          <Link to="/login">
            <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
              Already have an account? Sign In
            </Button>
          </Link>
        </div>
      </nav>

      <div className="pt-28 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Back button */}
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* User Limit Alert Banner */}
          {upgradeReason === 'user_limit' && (
            <Alert className="mb-6 bg-amber-500/10 border-amber-500/30 text-amber-200">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              <AlertTitle className="text-amber-400 font-semibold">User Limit Reached</AlertTitle>
              <AlertDescription className="text-amber-200/80">
                Your company has reached the maximum number of users on the Free plan. 
                Upgrade to Pro to add more team members and unlock all features.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left: Plan Summary */}
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {upgradeReason === 'user_limit' ? 'Upgrade to Pro' : 'Subscribe to CONNECT'}
              </h1>
              <p className="text-slate-400 mb-8">
                {upgradeReason === 'user_limit' 
                  ? 'Add more users and unlock premium features'
                  : 'Start your 14-day free trial today'}
              </p>

              {/* User Count Selector */}
              <Card className="bg-slate-800/50 border-slate-700/50 mb-6">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-cyan-400" />
                    Number of Users
                  </CardTitle>
                  <CardDescription>Select how many team members will use CONNECT</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Button
                        variant="outline"
                        size="icon"
                        className="border-slate-600 hover:bg-slate-700"
                        onClick={() => setUserCount(Math.max(1, userCount - 1))}
                        disabled={userCount <= 1}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="text-3xl font-bold text-white w-16 text-center">{userCount}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="border-slate-600 hover:bg-slate-700"
                        onClick={() => setUserCount(userCount + 1)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-white">${totalPrice}/mo</div>
                      <div className="text-sm text-slate-400">${pricePerUser}/user/month</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Features List */}
              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-white">What's included</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-slate-300">
                        <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0">
                          <Check className="w-3 h-3 text-cyan-400" />
                        </div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Trust Badges */}
              <div className="flex items-center gap-4 mt-6 text-slate-500 text-sm">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Secure payment
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Cancel anytime
                </div>
              </div>
            </div>

            {/* Right: Payment Form */}
            <div>
              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-cyan-400" />
                    Payment Details
                  </CardTitle>
                  <CardDescription>Your card won't be charged until after the 14-day trial</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Company & Contact Info */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Company Name *</Label>
                      <Input
                        placeholder="Your Company Ltd"
                        value={paymentForm.companyName}
                        onChange={(e) => setPaymentForm({ ...paymentForm, companyName: e.target.value })}
                        className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Your Name *</Label>
                        <Input
                          placeholder="John Smith"
                          value={paymentForm.name}
                          onChange={(e) => setPaymentForm({ ...paymentForm, name: e.target.value })}
                          className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Email *</Label>
                        <Input
                          type="email"
                          placeholder="you@company.com"
                          value={paymentForm.email}
                          onChange={(e) => setPaymentForm({ ...paymentForm, email: e.target.value })}
                          className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator className="bg-slate-700" />

                  {/* Card Details (Mock) */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Card Number</Label>
                      <Input
                        placeholder="4242 4242 4242 4242"
                        value={paymentForm.cardNumber}
                        onChange={(e) => setPaymentForm({ ...paymentForm, cardNumber: formatCardNumber(e.target.value) })}
                        maxLength={19}
                        className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 font-mono"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Expiry</Label>
                        <Input
                          placeholder="MM/YY"
                          value={paymentForm.expiry}
                          onChange={(e) => setPaymentForm({ ...paymentForm, expiry: formatExpiry(e.target.value) })}
                          maxLength={5}
                          className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">CVC</Label>
                        <Input
                          placeholder="123"
                          value={paymentForm.cvc}
                          onChange={(e) => setPaymentForm({ ...paymentForm, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                          maxLength={4}
                          className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Mock Payment Notice */}
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-sm text-amber-300">
                    <strong>Test Mode:</strong> Use card number <code className="bg-slate-800 px-1 rounded">4242 4242 4242 4242</code> with any future date and CVC.
                  </div>

                  <Separator className="bg-slate-700" />

                  {/* Order Summary */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-slate-400">
                      <span>{userCount} user{userCount > 1 ? 's' : ''} × ${pricePerUser}/month</span>
                      <span>${totalPrice}/mo</span>
                    </div>
                    <div className="flex justify-between text-slate-400">
                      <span>14-day free trial</span>
                      <span className="text-emerald-400">-${totalPrice}</span>
                    </div>
                    <Separator className="bg-slate-700" />
                    <div className="flex justify-between text-white font-semibold text-lg">
                      <span>Due today</span>
                      <span>$0.00</span>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white py-6 text-lg shadow-lg shadow-cyan-500/25"
                    onClick={handlePayment}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Start Free Trial
                        <Lock className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    By subscribing, you agree to our Terms of Service and Privacy Policy.
                    You can cancel anytime during or after your trial.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
